import React, { useState } from "react";
import { Card, CardBody, CardHeader, Button } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useUser } from "@webuild-ai/react-auth";

import { ConfirmationDialog } from "@/components/UI/ConfirmationDialog";
import { useConfirmationDialog } from "@/hooks/useConfirmationDialog";
import { FeatureCardProps } from "@/components/Chat/FeatureCards";
import { logger } from "@/telemetry";

import { ShortcutDisplay } from "./ShortcutDisplay";
import { ShortcutEditForm } from "./ShortcutEditForm";

interface ValidationErrors {
  key?: string;
  title?: string;
  descriptions?: string;
}

export const PromptShortcutsSettings: React.FC = () => {
  const { t } = useTranslation();
  const { userProfile, updateUserProfile } = useUser();
  const { isOpen, showConfirmation, confirmationProps } = useConfirmationDialog();

  const [promptShortcuts, setPromptShortcuts] = useState<FeatureCardProps[]>(
    userProfile?.config?.promptShortcuts || [],
  );
  const [editingShortcutIndex, setEditingShortcutIndex] = useState<number | null>(null);
  const [editingShortcut, setEditingShortcut] = useState<FeatureCardProps | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const validateShortcut = (shortcut: FeatureCardProps): boolean => {
    const errors: ValidationErrors = {};

    if (!shortcut.key.trim()) {
      errors.key = t("Key is required");
    }

    if (!shortcut.title.trim()) {
      errors.title = t("Title is required");
    }

    const validDescriptions = shortcut.descriptions.filter((d) => d.trim());

    if (validDescriptions.length === 0) {
      errors.descriptions = t("At least one description is required");
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const isFormValid =
    editingShortcut &&
    editingShortcut.key.trim() &&
    editingShortcut.title.trim() &&
    editingShortcut.descriptions.some((d) => d.trim());

  const handleAddShortcut = () => {
    const newShortcut: FeatureCardProps = { key: "", title: "", icon: "", descriptions: [""] };

    setPromptShortcuts([...promptShortcuts, newShortcut]);
    setEditingShortcutIndex(promptShortcuts.length);
    setEditingShortcut(newShortcut);
    setValidationErrors({});
  };

  const handleEditShortcut = (shortcut: FeatureCardProps, index: number) => {
    setEditingShortcutIndex(index);
    setEditingShortcut({ ...shortcut });
    setValidationErrors({});
  };

  const handleSaveShortcut = async () => {
    if (editingShortcutIndex !== null && editingShortcut) {
      if (!validateShortcut(editingShortcut)) {
        return;
      }

      const updatedShortcuts = [...promptShortcuts];

      updatedShortcuts[editingShortcutIndex] = editingShortcut;
      setPromptShortcuts(updatedShortcuts);
      setEditingShortcutIndex(null);
      setEditingShortcut(null);
      setValidationErrors({});

      try {
        await updateUserProfile({
          config: {
            ...(userProfile?.config || {}),
            promptShortcuts: updatedShortcuts,
          },
        });
      } catch (error) {
        logger.error("Failed to save prompt shortcut", error);
      }
    }
  };

  const handleCancelEdit = () => {
    if (
      editingShortcutIndex !== null &&
      editingShortcutIndex === promptShortcuts.length - 1 &&
      !promptShortcuts[editingShortcutIndex].key &&
      !promptShortcuts[editingShortcutIndex].title
    ) {
      setPromptShortcuts(promptShortcuts.slice(0, -1));
    }
    setEditingShortcutIndex(null);
    setEditingShortcut(null);
    setValidationErrors({});
  };

  const handleRemoveShortcut = async (index: number) => {
    const shortcutToRemove = promptShortcuts[index];
    const confirmed = await showConfirmation({
      title: t("Remove Shortcut"),
      message: t(
        'Are you sure you want to remove the shortcut "{{title}}"? This action cannot be undone.',
        {
          title: shortcutToRemove?.title || t("Untitled"),
        },
      ),
      confirmText: t("Remove"),
      cancelText: t("Cancel"),
      confirmColor: "danger",
      icon: "solar:trash-bin-trash-bold",
    });

    if (!confirmed) {
      return;
    }

    const updatedShortcuts = promptShortcuts.filter((_, i) => i !== index);

    setPromptShortcuts(updatedShortcuts);

    try {
      await updateUserProfile({
        config: {
          ...(userProfile?.config || {}),
          promptShortcuts: updatedShortcuts,
        },
      });
    } catch (error) {
      logger.error("Failed to remove prompt shortcut", error);
    }
  };

  const handleShortcutChange = (shortcut: FeatureCardProps) => {
    setEditingShortcut(shortcut);
  };

  const handleValidationErrorClear = (field: keyof ValidationErrors) => {
    setValidationErrors({ ...validationErrors, [field]: undefined });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {t("Prompt Shortcuts")}
            </h2>
            <Button className="btn-brand-primary" onClick={handleAddShortcut}>
              {t("Add Shortcut")}
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid gap-6">
            {promptShortcuts.map((shortcut, index) => (
              <div
                key={index}
                className="p-4 border rounded-lg bg-gray-50 dark:bg-default-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
              >
                <div className="flex-1">
                  {editingShortcutIndex === index && editingShortcut ? (
                    <ShortcutEditForm
                      isFormValid={!!isFormValid}
                      shortcut={editingShortcut}
                      validationErrors={validationErrors}
                      onCancel={handleCancelEdit}
                      onSave={handleSaveShortcut}
                      onShortcutChange={handleShortcutChange}
                      onValidationErrorClear={handleValidationErrorClear}
                    />
                  ) : (
                    <ShortcutDisplay shortcut={shortcut} />
                  )}
                </div>
                <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-end min-w-fit">
                  {editingShortcutIndex !== index && (
                    <Button
                      aria-label={t("Edit shortcut")}
                      variant="light"
                      onClick={() => handleEditShortcut(shortcut, index)}
                    >
                      {t("Edit")}
                    </Button>
                  )}
                  <Button
                    aria-label={t("Remove shortcut")}
                    color="danger"
                    variant="light"
                    onClick={() => handleRemoveShortcut(index)}
                  >
                    {t("Remove")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
      <ConfirmationDialog isOpen={isOpen} {...confirmationProps} />
    </>
  );
};
